import apollo from '@/graphql'

import cache from '@/store/helpers/cache.helper'

import moment from 'moment'

import StockcheckMetricsQuery from '@/graphql/metrics/StockcheckMetricsQuery.gql'
import StoreMetricsQuery from '@/graphql/metrics/StoreMetricsQuery.gql'
import ProductMetricsQuery from '@/graphql/metrics/ProductMetricsQuery.gql'
import RetailerMetricsQuery from '@/graphql/metrics/RetailerMetricsQuery.gql'
import TrendMetricsQuery from '@/graphql/metrics/TrendMetricsQuery.gql'
import TotalIssuesResolvedMetricsQuery from '@/graphql/metrics/TotalIssuesResolvedMetricsQuery.gql'
import IssuesResolvedThisMonthMetricsQuery from '@/graphql/metrics/IssuesResolvedThisMonthMetricsQuery.gql'

function initialState() {
  return {
    isLoading: {
      StoreMetricsQuery: false,
      ProductMetricsQuery: false,
      RetailerMetricsQuery: false,
      TrendMetricsQuery: false,
      TotalIssuesResolvedMetricsQuery: false,
      IssuesResolvedThisMonthMetricsQuery: false,
    },
    metrics: {},
  }
}

const query = {
  Store: 'StoreMetricsQuery',
  Product: 'ProductMetricsQuery',
  Retailer: 'RetailerMetricsQuery',
  Trend: 'TrendMetricsQuery',
  TotalIssuesResolved: 'TotalIssuesResolvedMetricsQuery',
  IssuesResolvedThisMonth: 'IssuesResolvedThisMonthMetricsQuery',
}

const state = initialState()

const getters = {
  bestStockProduct: (st) => {
    if (!st.metrics.productMetrics) {
      return { product: '' }
    }

    const products = st.metrics.productMetrics

    const bestScoreIx = Object.keys(products)
      .reduce(
        (maxIx, ix) => {
          if (
            parseInt(products[maxIx].inStock, 10)
              > parseInt(products[ix].inStock, 10)
          ) {
            return maxIx
          }

          return ix
        },
        0,
      )

    return products[bestScoreIx] || { product: '' }
  },

  bestStockStore: (st) => {
    if (!st.metrics.storeMetrics) {
      return { store: '' }
    }

    const highestScore = st.metrics.storeMetrics
      .reduce((score, store) => Math.max(score, parseInt(store.inStock, 10)), 0)

    // arbitrarily picking the first store in the list
    const [bestStore] = st.metrics.storeMetrics
      .filter(store => parseInt(store.inStock, 10) === highestScore)

    // worst store includes id
    return bestStore || { store: '' }
  },

  complianceRate: (st) => {
    const arr = st.metrics.trends
    if (!arr || !arr.length) return ''

    const dataPoint = arr[arr.length - 1]
    return Math.round(100 - (100 * (dataPoint.compliance / dataPoint.products)))
  },

  auditHistoryChartData: (st) => {
    if (!st.metrics.trends) return {}

    return {
      labels: st.metrics.trends.map(dataPoint => moment.unix(dataPoint.ts / 1000).format('MM/DD')),
      datasets: [
        {
          backgroundColor: '#03c7a4',
          borderColor: '#03c7a4',
          borderWidth: 0,
          fill: true,
          label: 'Out of Stock Issues',
          // Stock rate in percent
          // Should have the same number of entries as the legend above
          data: st.metrics.trends.map(dataPoint => Math.round(dataPoint.outOfStocks)),
        },
        {
          backgroundColor: '#f6db23',
          borderColor: '#f6db23',
          borderWidth: 2,
          fill: true,
          label: 'Compliance Issues',
          // Stock rate in percent
          // Should have the same number of entries as the legend above
          data: st.metrics.trends.map(dataPoint => Math.round(dataPoint.compliance)),
        },
      ],
    }
  },

  estSalesLiftChartData: (st) => {
    if (!st.metrics.trends) return {}

    return {
      labels: st.metrics.trends.map(dataPoint => moment.unix(dataPoint.ts / 1000).format('MM/DD')),
      datasets: [
        {
          label: 'Est. Lift',
          backgroundColor: '#02347c',
          borderColor: '#02347c',
          fill: false,
          /**
           * $144 saved per fix per year
           * Popspots causes issues to be fixed 1 week earlier on average
           */
          data: st.metrics.trends
            .map(dataPoint => dataPoint.resolved * (144 / 52)),
        },
      ],
    }
  },

  estimatedLift: (st) => {
    if (!st.metrics.trends) {
      return ''
    }

    /**
     * $144 saved per fix per year
     * Popspots causes issues to be fixed 1 week earlier on average
     */
    return st.metrics.totalIssuesResolved * (144 / 52)
  },

  inStockAndComplianceChartData: (st) => {
    // X-axis time data
    // Should come from sc_metrics timestamps
    if (!st.metrics.trends) return {}

    return {
      labels: st.metrics.trends.map(dataPoint => moment.unix(dataPoint.ts / 1000).format('MM/DD')),
      datasets: [
        {
          backgroundColor: 'white',
          borderColor: '#59c6a1',
          pointBackgroundColor: '#59c6a1',
          fill: false,
          label: 'In Stock',
          lineTension: 0.0,
          pointRadius: 3,
          // Stock rate in percent
          // Should have the same number of entries as the legend above
          data: st.metrics.trends.map(
            dataPoint => 100 - (100 * (dataPoint.outOfStocks / dataPoint.products)),
          ),
        },
        {
          backgroundColor: 'white',
          borderColor: '#054db6',
          pointBackgroundColor: '#054db6',
          fill: false,
          label: 'Compliance',
          lineTension: 0.0,
          pointRadius: 3,
          // Stock rate in percent
          // Should have the same number of entries as the legend above
          data: st.metrics.trends.map(
            dataPoint => 100 - (100 * (dataPoint.compliance / dataPoint.products)),
          ),
        },
      ],
    }
  },

  potentialLift: (st) => {
    const arr = st.metrics.trends
    if (!arr || !arr.length) return ''

    const dataPoint = arr[arr.length - 1]
    // this metric per Dallas on Thurs, Oct. 25, 2018
    return dataPoint.outOfStocks * 144
  },

  stockComplianceChartData: (st) => {
    // X-axis time data
    // Should come from sc_metrics timestamps
    if (!st.metrics.trends) return {}

    return {
      labels: st.metrics.trends.map(dataPoint => moment.unix(dataPoint.ts / 1000).format('MM/DD')),
      datasets: [
        {
          backgroundColor: 'white',
          borderColor: '#246bd8',
          pointBackgroundColor: '#246bd8',
          borderWidth: 2,
          fill: false,
          label: 'Compliant',
          lineTension: 0.0,
          pointRadius: 2,
          // Stock rate in percent
          // Should have the same number of entries as the legend above
          data: st.metrics.trends.map(
            dataPoint => 100 - (100 * (dataPoint.compliance / dataPoint.products)),
          ),
        },
        {
          backgroundColor: 'white',
          borderColor: '#03c7a4',
          pointBackgroundColor: '#03c7a4',
          borderWidth: 2,
          fill: false,
          label: 'In Stock',
          lineTension: 0.0,
          pointRadius: 2,
          // Stock rate in percent
          // Should have the same number of entries as the legend above
          data: st.metrics.trends.map(
            dataPoint => 100 - (100 * (dataPoint.outOfStocks / dataPoint.products)),
          ),
        },
      ],
    }
  },

  stockRate: (st) => {
    const arr = st.metrics.trends
    if (!arr || !arr.length) return ''

    const dataPoint = arr[arr.length - 1]
    return Math.round(100 - (100 * (dataPoint.outOfStocks / dataPoint.products)))
  },

  worstStockProduct: (st) => {
    if (!st.metrics.productMetrics) {
      return { product: '' }
    }

    const products = st.metrics.productMetrics

    const lowestScoreIx = Object.keys(products)
      .reduce(
        (lowIx, ix) => {
          if (
            parseInt(products[lowIx].inStock, 10)
              < parseInt(products[ix].inStock, 10)
          ) {
            return lowIx
          }

          return ix
        },
        0,
      )

    return products[lowestScoreIx] || { product: '' }
  },

  worstStockRetailer: (st) => {
    if (!st.metrics.retailerMetrics) return { retailer: '' }

    const lowestScore = st.metrics.retailerMetrics
      .reduce((score, retailer) => Math.min(score, parseInt(retailer.inStock, 10)), 100)

    // arbitrarily picking the first retailer in the list
    const [worstRetailer] = st.metrics.retailerMetrics
      .filter(retailer => parseInt(retailer.inStock, 10) === lowestScore)

    // worst retailer includes id
    return worstRetailer || { retailer: '' }
  },

  worstStockStore: (st) => {
    if (!st.metrics.storeMetrics) return { store: '' }

    const lowestScore = st.metrics.storeMetrics
      .reduce((score, store) => Math.min(score, parseInt(store.inStock, 10)), 100)

    // arbitrarily picking the first store in the list
    const [worstStore] = st.metrics.storeMetrics
      .filter(store => parseInt(store.inStock, 10) === lowestScore)

    // worst store includes id
    return worstStore || { store: '' }
  },
}

const mutations = {
  initializeStore() {
    state.metrics = cache.get('stockcheckMetrics') || {}
  },

  reset() {
    const s = initialState()
    Object.keys(s).forEach((key) => {
      state[key] = s[key]
    })
    cache.remove('stockcheckMetrics')
  },

  setStockcheckMetrics(st, metrics) {
    state.metrics = metrics
  },

  setStockcheckMetricsLoading(state, { loading, queryName }) {
    state.isLoading[queryName] = loading
  },
}

const actions = {
  init({ commit }) {
    commit('initializeStore')
  },

  async load({ commit }, { variables, queryName, refreshCache }) {
    if (state.isLoading && state.isLoading[queryName]) {
      /**
       * Prevent redundant data calls
       */
      return
    }

    const hasFilter = variables.filter.retailersIds.length
    const hasCache = (name) => {
      const stockcheckMetrics = cache.get('stockcheckMetrics')
      switch (name) {
        case query.Store:
          return stockcheckMetrics && stockcheckMetrics.storeMetrics
        case query.Product:
          return stockcheckMetrics && stockcheckMetrics.productMetrics
        case query.Retailer:
          return stockcheckMetrics && stockcheckMetrics.retailerMetrics
        case query.Trend:
          return stockcheckMetrics && stockcheckMetrics.trends
        case query.TotalIssuesResolved:
          return stockcheckMetrics && stockcheckMetrics.totalIssuesResolved
        case query.IssuesResolvedThisMonth:
          return stockcheckMetrics && stockcheckMetrics.issuesResolvedThisMonth
        default:
          return undefined
      }
    }

    if (hasFilter || !hasCache(queryName)) {
      commit('setStockcheckMetricsLoading', { loading: true, queryName })
    }

    const getMetricsQuery = (name) => {
      switch (name) {
        case query.Store:
          return StoreMetricsQuery
        case query.Product:
          return ProductMetricsQuery
        case query.Retailer:
          return RetailerMetricsQuery
        case query.Trend:
          return TrendMetricsQuery
        case query.TotalIssuesResolved:
          return TotalIssuesResolvedMetricsQuery
        case query.IssuesResolvedThisMonth:
          return IssuesResolvedThisMonthMetricsQuery
        default:
          return StockcheckMetricsQuery
      }
    }
    const result = await apollo.defaultClient.query({
      query: getMetricsQuery(queryName),
      fetchPolicy: refreshCache ? 'network-only' : 'cache-first',
      variables,
    }).catch((error) => {
      // eslint-disable-next-line no-console
      console.log(error)
      commit('setStockcheckMetricsLoading', { loading: false, queryName })
    })

    commit('setStockcheckMetricsLoading', { loading: false, queryName })

    if (!result) return

    const convertToStockcheckMetrics = (metrics, name, data) => {
      switch (name) {
        case query.Store:
          return { ...metrics, storeMetrics: data.storeMetrics }
        case query.Product:
          return { ...metrics, productMetrics: data.productMetrics }
        case query.Retailer:
          return { ...metrics, retailerMetrics: data.retailerMetrics }
        case query.Trend:
          return { ...metrics, trends: data.trends }
        case query.TotalIssuesResolved:
          return {
            ...metrics,
            totalIssuesResolved: data.totalIssuesResolved,
          }
        case query.IssuesResolvedThisMonth:
          return {
            ...metrics,
            issuesResolvedThisMonth: data.issuesResolvedThisMonth,
          }
        default:
          return data.stockcheckMetrics
      }
    }
    const stockcheckMetrics = convertToStockcheckMetrics(state.metrics, queryName, result.data)

    if (!hasFilter) {
      cache.set('stockcheckMetrics', stockcheckMetrics)
    }

    commit('setStockcheckMetrics', stockcheckMetrics)
  },

  reset({ commit }) {
    commit('reset')
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}



// WEBPACK FOOTER //
// ./src/store/modules/metrics/StockcheckMetrics.js